/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { MessageBar } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FontIcon, Text } from '@bingads-webui-react/fabric-control';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { TaskManager } from '@bingads-webui-universal/task-manager';
import { useCopilotLoggers, useInitCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { VideoPanel } from './video-panel';
import { PlacementQualities } from '../ad-components';
import { Link, TextField } from '../common/shared-components';
import { getAdsStepStyles } from './styles';
import { usePageManager } from './page-manager';

// TODO: Upload video styling for smaller videos.
// TODO: Upload video styling for larger videos.
// TODO: Add background of video while uploading.
// TODO: Add label for processing.
// TODO: Add progress video background
// TODO: Add suggested placements component next to the video.


// TODO: Add dots for Ad Preview pagination - DONE


export const VideoAdsContainer = (props) => {
  const {
    aid,
    cid,
    i18n,
    perfMarker,
    onAdsValidation,
    currentVideoIndex,
    ads = [],
    oDataConfig,
    disablePagination,
    permissions,
    deal,
    dealLoaded,
    customStyleFunction,
    useCustomStyleFunction,
    resetFields,
    isAdGridEditorMode,
    allowCopilotVideoGeneration,
    biddingScheme = {},
    campaignId,
    hideFooter,
    isXandrServingCampaign,
    loadClipchampPlayer,
  } = props;

  const videoRequirementForDeal = _.get(deal, ['DealAdQualityConstraints', '0', 'VideoQuality']);

  const isForDeal = !_.isEmpty(deal);

  let dealVideoRequirementText;
  if (videoRequirementForDeal === PlacementQualities.ONLY_PERFECT) {
    dealVideoRequirementText = i18n.getString(_TL_('Your campaign\'s media deal requires that your video is of excellent quality (three stars). For more information about the technical requirements, see '));
  } else if (videoRequirementForDeal === PlacementQualities.ONLY_EXCELLENT) {
    dealVideoRequirementText = i18n.getString(_TL_('For this deal your asset must meet the General Video requirements.'));
  }

  const isEditScenario = useMemo(() => isAdGridEditorMode && ads && ads[0] && ads[0].Id, [ads, isAdGridEditorMode]);

  useInitCopilotLoggers({ campaignId, feature: 'VideoAds', isEditScenario });

  const { logEnterFlow } = useCopilotLoggers();

  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const activity = useContext(ActivityContext);

  const customClasses = useDefaultStyles(customStyleFunction);

  const classes = useCustomStyleFunction ? customClasses : defaultClasses;

  const [videoAds, setVideoAds] = useState(ads || []);

  const [useBrandKit, setUseBrandKit] = useState(true);

  const copilotFetchTaskManager = useMemo(() => new TaskManager(), []);

  const serverContext = useContext(ServerContext);
  const [isAdsCopilotRegionRestricted, setIsAdsCopilotRegionRestricted] = useState(true);
  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Recommendations',
      permissions,
      serverContext,
    }).then(setIsAdsCopilotRegionRestricted);
  }, [permissions, serverContext]);

  const copilotEnabled = allowCopilotVideoGeneration &&
    permissions.dynamic.VideoCopilotGeneration &&
    !isAdsCopilotRegionRestricted &&
    (!videoRequirementForDeal || videoRequirementForDeal < PlacementQualities.ONLY_PERFECT);

  const {
    goToNextPage,
    goToPrevPage,
    goToPage,
    getPageAdjustedIndex,
    currentPage,
    currentPageIndex,
    pages,
    onAddItem,
    removeItem,
    totalPages,
    maxItems,
  } = usePageManager(ads);

  const onAddAd = () => {
    onAddItem();
    if (activity) {
      activity.signal(`Adding new ad panel - current total: ${currentPage.numberOfAdsInPage}`);
    }
  };

  const onVideoPanelUpdate = (result, idx, uid) => {
    let itemAtIdx = videoAds[idx] || { video: null, FinalUrls: [], uid: null };

    if (itemAtIdx.uid === null) {
      itemAtIdx = { ...result, uid, Id: itemAtIdx.Id };
    } else {
      itemAtIdx = { ...result, uid: itemAtIdx.uid, Id: itemAtIdx.Id };
    }

    const itemsBefore = videoAds.slice(0, idx);
    const itemsAfter = videoAds.slice(idx + 1, videoAds.length);
    setVideoAds([...itemsBefore, { ...itemAtIdx }, ...itemsAfter]);
  };

  const onRemovePanel = (idx) => {
    if (activity) {
      activity.signal(`Removing ad panel at idx ${idx}`);
    }
    videoAds.splice(idx, 1);

    setVideoAds([...videoAds]);

    removeItem();
  };

  const isAllValid = () => {
    let valid = true;
    // eslint-disable-next-line consistent-return
    _.each(videoAds, (ad) => {
      if (!ad.isValid) {
        valid = false;
      }
    });

    return valid;
  };

  const isAllDirty = () => {
    let dirty = false;
    // eslint-disable-next-line consistent-return
    _.each(videoAds, (ad) => {
      if (ad.isDirty) {
        dirty = true;
      }
    });

    return dirty;
  };

  useEffect(() => {
    if (activity) {
      activity.signal(`[VideoAdsCopilot] - useBrandKitToggle changed to ${useBrandKit}`);
    }
  }, [useBrandKit, activity]);

  useEffect(() => {
    const result = {
      data: [...videoAds], isValid: isAllValid(), isDirty: isAllDirty(), currentPageIndex,
    };
    onAdsValidation(result);
    if (activity) {
      activity.signal(`onVideoAdsContainerValidated - ${JSON.stringify(result)}`);
    }
  // disabling exhaustive deps since the onAdsValidation value changes on each rerender.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageIndex, videoAds]);

  useEffect(() => {
    if (resetFields) {
      setVideoAds([]);
    }
  }, [resetFields]);

  useEffect(() => {
    setClarityTag('XandrVideoAds', 'true');
  }, []);

  useEffect(() => {
    if (!_.isEqual(ads, videoAds) && !isAdGridEditorMode) {
      // check if ads has any ad that has id. (update scenario)
      let shouldUpdate = false;

      _.each(ads, (ad) => {
        const videoAd = _.filter(videoAds, vAd => vAd.uid === ad.uid);

        if (videoAd && videoAd[0] && _.isUndefined(videoAd[0].Id) && ad.Id) {
          shouldUpdate = true;
        }

        // For reload scenario if we can't find any ad in local state, refresh state
        if (permissions.dynamic.MultiAdFormat && _.isEmpty(videoAd) && ad.Id) {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        setVideoAds(ads);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads]);

  useEffect(() => {
    if (copilotEnabled) {
      logEnterFlow();
    }
  }, [copilotEnabled, logEnterFlow]);

  return (
    <div className={classes.rootVideoAdsContainer}>
      {
        dealVideoRequirementText &&
        <MessageBar className="deal-video-quality-message" styles={{ root: { background: '#EFF6FC' } }}>
          {dealVideoRequirementText}
          {(videoRequirementForDeal === PlacementQualities.ONLY_PERFECT) &&
          <Link href="https://help.ads-int.microsoft.com/#apex/ads/en/60102/0/en/#ext:createcampaign_awareness" target="_blank">
            {i18n.getString(_TL_('About video ads'))}.
          </Link>}
        </MessageBar>
      }
      {
        _.map(pages, (page) => {
          const panels = _.map(_.range(page.numberOfAdsInPage), (idx) => {
            const videoAd = (videoAds[getPageAdjustedIndex(idx, page.pageNumber)] && videoAds[getPageAdjustedIndex(idx, page.pageNumber)].data);

            const uid = (videoAds[getPageAdjustedIndex(idx, page.pageNumber)] && videoAds[getPageAdjustedIndex(idx, page.pageNumber)].uid) || Math.random();

            const isCurrentPage = page.id === currentPage.id;

            return (
              <div style={{ display: isCurrentPage ? 'block' : 'none' }} className={`video-panel-idx-${idx}`}>
                <VideoPanel
                  cid={cid}
                  aid={aid}
                  campaignId={campaignId}
                  copilotFetchTaskManager={copilotFetchTaskManager}
                  key={`${uid}-videoPanel`}
                  i18n={i18n}
                  perfMarker={perfMarker}
                  permissions={permissions}
                  videoAd={videoAd}
                  onUpdate={result => onVideoPanelUpdate(result, getPageAdjustedIndex(idx, page.pageNumber), uid)}
                  onRemovePanel={() => onRemovePanel(getPageAdjustedIndex(idx, page.pageNumber), page)}
                  removeCloseButton={page.numberOfAdsInPage === 1}
                  keyIndex={uid}
                  videoRequirementForDeal={videoRequirementForDeal}
                  isSelectedPanel={currentVideoIndex === idx}
                  oDataConfig={oDataConfig}
                  biddingScheme={biddingScheme}
                  isXandrServingCampaign={isXandrServingCampaign}
                  isForDeal={isForDeal}
                  dealLoaded={dealLoaded}
                  isAdGridEditorMode={isAdGridEditorMode}
                  copilotEnabled={copilotEnabled}
                  loadClipchampPlayer={loadClipchampPlayer}
                  useBrandKit={useBrandKit}
                  setUseBrandKit={setUseBrandKit}
                />
              </div>
            );
          });

          return panels;
        })
      }
      <div style={{ marginTop: 10 }}>
        {
          (!hideFooter && (currentPage.numberOfAdsInPage < maxItems && !disablePagination)) && (
            <Link className="add-new-ad" onClick={onAddAd}>
              <FontIcon iconName="Add" />&nbsp;{i18n.getString(_TL_('Create another ad'))}
            </Link>
          )
        }
      </div>
      {totalPages > 1 && !disablePagination && (
      <div className={classes.pagination}>
        <div className={classes.pageControls}>
          <Link
            onClick={goToPrevPage}
            disabled={currentPageIndex <= 1}
          >
            <FontIcon iconName="ChevronLeft" /> <Text> Prev </Text>
          </Link>
          <TextField
            styles={{ root: { width: 40 } }}
            value={currentPageIndex}
            onChange={(e, val) => goToPage(val)}
          />
          <span> of {totalPages} </span>
          <Link
            onClick={goToNextPage}
            disabled={currentPageIndex === totalPages}
          >
            <Text> Next </Text> <FontIcon iconName="ChevronRight" />
          </Link>
        </div>
      </div>
          )}
    </div>
  );
};


VideoAdsContainer.propTypes = {
  aid: PropTypes.string.isRequired,
  cid: PropTypes.string.isRequired,
  biddingScheme: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      VideoCopilotGeneration: PropTypes.bool,
    }),
  }).isRequired,
  onAdsValidation: PropTypes.func.isRequired,
  ads: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  oDataConfig: PropTypes.shape({}),
  disablePagination: PropTypes.bool,
  customStyleFunction: PropTypes.func,
  useCustomStyleFunction: PropTypes.bool,
  resetFields: PropTypes.bool,
  currentVideoIndex: PropTypes.number,
  isAdGridEditorMode: PropTypes.bool,
  hideFooter: PropTypes.bool,
  deal: PropTypes.shape({}),
  dealLoaded: PropTypes.bool,
  allowCopilotVideoGeneration: PropTypes.bool,
  campaignId: PropTypes.number,
  isXandrServingCampaign: PropTypes.bool,
};

VideoAdsContainer.defaultProps = {
  oDataConfig: null,
  disablePagination: false,
  customStyleFunction: _.noop,
  useCustomStyleFunction: false,
  deal: null,
  dealLoaded: false,
  resetFields: false,
  hideFooter: false,
  currentVideoIndex: 0,
  isAdGridEditorMode: false,
  allowCopilotVideoGeneration: false,
  campaignId: -1,
  isXandrServingCampaign: false,
};
